import {
  curry,
  not,
  isEmpty,
  reduce,
  either,
  isNil,
  includes,
  defaultTo,
  equals,
  complement,
  addIndex,
  map,
  length,
  flip,
  prop,
} from 'ramda'
import { isUndefined } from 'utils/types'

// sumProp :: String -> Object -> Number
const sumProp = curry(
  (property, obj) => obj && obj.reduce((acc, item) => acc + item[property], 0)
)

// toMap :: String -> [Object] -> Map
const toMap = curry((key, collection) => {
  const formattedCollection = collection.map((item) => [item[key], item])

  return new Map(formattedCollection)
})

// keyedObject :: Array -> [Object] -> Array
const pickToArray = curry((keys, object) => {
  if (!object || !keys) return []

  return keys.reduce((acc, key) => {
    const item = object[key]

    if (!isUndefined(item)) return acc.push(item)

    return acc
  }, [])
})

// keyedObject :: String -> [Object] -> Object
const keyedObject = curry((key, arr) =>
  reduce(
    (accum, item) => ({
      ...accum,
      [item[key]]: item,
    }),
    {}
  )(arr)
)

// callOr :: a -> Function -> b -> (a || c)
const callOr = curry((defaultValue, fn, collection) =>
  collection && not(isEmpty(collection)) ? fn(collection) : defaultValue
)

const strictAnyResult = curry((fnsArr, arg) => {
  return fnsArr.reduce((accum, callback) => {
    if (not(isNil(accum))) {
      return accum
    }
    const result = callback(arg)
    if (not(isNil(result))) {
      return result
    }
    return accum
  }, null)
})

// isEmptyOrNil :: a -> Boolean
const isEmptyOrNil = either(isEmpty, isNil)
// toggle :: values -> Function -> value -> next-value-in-list
const toggle = (values) => {
  const toggleMap = values.reduce(
    (obj, value, i) => ({ ...obj, [value]: (i + 1) % values.length }),
    {}
  )
  return (value) => values[toggleMap[value]]
}

const sortListByKey = (key, dir = 'asc') => {
  const mult = dir.toLowerCase() === 'desc' ? 1 : -1
  return (a, b) => {
    if (a[key] < b[key]) return mult
    if (a[key] > b[key]) return mult * -1
    return 0
  }
}

// defaults checked item to an empty array
const safeIncludes = curry((value, list) =>
  includes(value, defaultTo([], list))
)

// checks if a item is unique in a list
const isUniq = (value, list) => list.filter(equals(value)).length === 1

const notEquals = complement(equals)

const mapFrom = (dictionary) => (term, defaultDisplay = '') =>
  dictionary[term] || defaultDisplay

const mapIndexed = addIndex(map)

const findDuplicates = curry((property, list) =>
  list.reduce((acc, curr) => {
    const currProp = prop(property, curr)
    const found = list.filter((item) => prop(property, item) === currProp)

    if (length(found) > 1)
      return {
        ...acc,
        [currProp]: found,
      }

    return acc
  }, {})
)

const flipProp = flip(prop)
const flipIncludes = flip(includes)

const chunkBy = curry((size, list) => {
  const chunkSize = size;
  const chunks = []
  for (let i = 0; i < list.length; i += chunkSize) {
    const chunk = list.slice(i, i + chunkSize)
    chunks.push(chunk)
  }
  return chunks
})

export {
  findDuplicates,
  keyedObject,
  sumProp,
  toMap,
  callOr,
  isEmptyOrNil,
  toggle,
  sortListByKey,
  safeIncludes,
  isUniq,
  pickToArray,
  notEquals,
  mapFrom,
  mapIndexed,
  flipProp,
  strictAnyResult,
  flipIncludes,
  chunkBy
}
